import Head from 'next/head';
import { useRouter } from 'next/router';
import { Fragment } from 'react';
import { ProductWithPrice } from 'types';

import { languageOptions } from './layout/LanguageSelector';
import StructuredData from './StructuredData';

// Change these default meta
const defaultMeta = {
  title: 'Easy-Peasy.AI: All-in-one AI Platform',
  siteName: 'Easy-Peasy.AI',
  description: '',
  /** Without additional '/' on the end */
  url: 'https://easy-peasy.ai',
  type: 'website',
  robots: 'follow, index',
  image: '',
};

type SeoProps = {
  date?: string;
  templateTitle?: string;
  products?: ProductWithPrice[];
  page?: 'home' | 'product' | 'other';
  productName?: string;
  productDescription?: string;
  productImage?: string[];
  includeFAQ?: boolean;
  /** Allow pages with their own accurate schema to skip generic product reviews. */
  structuredData?: boolean;
} & Partial<typeof defaultMeta>;

// Keep in step with the "redirect every language to English" rules in
// next.config.mjs.
const ENGLISH_ONLY_PAGE =
  /^\/(blog|affiliates|ai-workshops|plugins|privacy-policy|terms-of-use|interior-design-styles)(\/|$)/;

export default function Seo(props: SeoProps) {
  const router = useRouter();

  const meta = {
    ...defaultMeta,
    ...props,
  };
  meta['title'] = props.templateTitle
    ? `${props.templateTitle} | ${meta.siteName}`
    : meta.title;

  const cleanPath = router.asPath.split(/[?#]/)[0].replace(/\/$/, '');

  const contentLocale = router.locale || 'en';
  const canonicalUrl = `${meta.url}${
    contentLocale !== 'en' ? `/${contentLocale}` : ''
  }${cleanPath}`;

  return (
    <Head>
      <title>{meta.title}</title>
      <meta name='robots' content={meta.robots} />
      <meta content={meta.description} name='description' />
      <meta property='og:url' content={canonicalUrl} />
      <link rel='canonical' href={canonicalUrl} />
      {/* Open Graph */}
      <meta property='og:type' content={meta.type} />
      <meta property='og:site_name' content={meta.siteName} />
      <meta property='og:description' content={meta.description} />
      <meta property='og:title' content={meta.title} />
      <meta name='image' property='og:image' content={meta.image} />
      {/* Twitter */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:site' content='@easy_peasy_ai' />
      <meta name='twitter:title' content={meta.title} />
      <meta name='twitter:description' content={meta.description} />
      <meta name='twitter:image' content={meta.image} />
      {meta.date && (
        <>
          <meta property='article:published_time' content={meta.date} />
          <meta
            name='publish_date'
            property='og:publish_date'
            content={meta.date}
          />
        </>
      )}

      {/* Favicons */}
      <link
        rel='icon'
        type='image/png'
        href='/favicon-96x96.png'
        sizes='96x96'
      />
      {/* Removed favicon.svg - it was 278KB due to embedded PNG base64 */}
      <link rel='shortcut icon' href='/favicon/favicon.ico' />
      <link
        rel='apple-touch-icon'
        sizes='180x180'
        href='/favicon/apple-touch-icon.png'
      />
      <meta name='apple-mobile-web-app-title' content='Easy-Peasy' />
      <link rel='manifest' href='/site.webmanifest' />
      <meta name='msapplication-TileColor' content='#ffffff' />
      <meta
        name='msapplication-TileImage'
        content='/favicon/ms-icon-144x144.png'
      />
      <meta name='theme-color' content='#ffffff' />
      {/* English-only pages (next.config.mjs redirects /fr/blog etc. to
          the English URL) get no language alternates. */}
      {!ENGLISH_ONLY_PAGE.test(cleanPath) &&
        !canonicalUrl.includes('images/') &&
        languageOptions.map((lang) => {
          if (lang.value === 'en') {
            return (
              <Fragment key={lang.value}>
                <link
                  rel='alternate'
                  href={`${meta.url}${cleanPath}`}
                  hrefLang='en'
                />
                <link
                  rel='alternate'
                  href={`${meta.url}${cleanPath}`}
                  hrefLang='x-default'
                />
              </Fragment>
            );
          } else {
            return (
              <link
                key={lang.value}
                rel='alternate'
                href={`${meta.url}/${lang.value}${cleanPath}`}
                hrefLang={lang.value}
              />
            );
          }
        })}

      {/* Structured Data */}
      {props.structuredData !== false && (
        <StructuredData
          products={props.products}
          page={props.page}
          productName={props.productName}
          productDescription={props.productDescription}
          productImage={props.productImage}
          includeFAQ={props.includeFAQ}
        />
      )}
    </Head>
  );
}

type Favicons = {
  rel: string;
  href: string;
  sizes?: string;
  type?: string;
};

// This is the default favicon, you can generate your own from https://www.favicon-generator.org/ then replace the whole /public/favicon folder
const favicons: Array<Favicons> = [
  {
    rel: 'apple-touch-icon',
    sizes: '57x57',
    href: '/favicon/apple-icon-57x57.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '60x60',
    href: '/favicon/apple-icon-60x60.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '72x72',
    href: '/favicon/apple-icon-72x72.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '76x76',
    href: '/favicon/apple-icon-76x76.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '114x114',
    href: '/favicon/apple-icon-114x114.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '120x120',
    href: '/favicon/apple-icon-120x120.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '144x144',
    href: '/favicon/apple-icon-144x144.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '152x152',
    href: '/favicon/apple-icon-152x152.png',
  },
  {
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: '/favicon/apple-icon-180x180.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '192x192',
    href: '/favicon/android-icon-192x192.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: '/favicon/favicon-32x32.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '96x96',
    href: '/favicon/favicon-96x96.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
    href: '/favicon/favicon-16x16.png',
  },
  {
    rel: 'manifest',
    href: '/favicon/manifest.json',
  },
];
